import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, withStyles } from '@material-ui/core';
import { compose } from 'recompose';
import { withUserProfile } from '../../userProfile/userProfileContext';
import { useNavigate, useLocation } from 'react-router-dom';
import AppTutorial from './AppTutorial';
import { getDataWithQueryString } from '../../api/componentAPIs';
import { AppLoading } from '../../loaders/loaders';
import { ERROR_MESSAGES } from './constants';

const styles = (theme) => ({
  dialog: {
    padding: 0,
    boxSizing: 'border-box',
    borderRadius: '2px',
    backgroundColor: theme.ModalBGColor,
    boxShadow: '2px 2px 4px black',
    minWidth: '800px',
    maxWidth: '800px'
  },
  dialogContent: {
    padding: 0,
    overflow: 'hidden',
    '&:first-child': {
      padding: 0
    }
  },
  closeButton: {
    color: theme.iconColor
  },
  loaderContainer: {
    height: 560
  }
});

const initialDialogState = {
  videoURLs: [],
  assessmentData: null,
  isLoading: true,
  errorMessage: ''
};

const TutorialDialog = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { classes, dialogClasses } = props;
  const { theme } = props.userData.userProfile || {};
  const params = new URLSearchParams(location.search);
  const componentId = params.get('componentId');
  const [state, setState] = useState(initialDialogState);
  useEffect(() => {
    if (componentId) {
      getDataWithQueryString('GetTutorialData', `componentId=${componentId}`).then(
        (response) => {
          if (response.status === 200) {
            const videoURLPromises = fetchVideoUrlFromBlob(response.data.videos);
            const assessmentDataPromise = fetch(response.data.assessmentData).then((res) =>
              res.json()
            );
            Promise.all([videoURLPromises, assessmentDataPromise])
              .then(([videoSrcUrls, assessmentData]) => {
                setState((prevState) => ({
                  ...prevState,
                  videoURLs: videoSrcUrls,
                  assessmentData: assessmentData,
                  isLoading: false
                }));
              })
              .catch((error) => {
                handleError(ERROR_MESSAGES.FETCH_ERROR); // Show this when fetching data from blob fails
              });
          } else {
            handleError(
              response?.response?.data?.error?.message ?? ERROR_MESSAGES.DATA_UNAVAILABLE
            ); // Show this error message when API returns error. Ex: No Tutorial data available in blob for given componentId
          }
        },
        (err) => {
          handleError(ERROR_MESSAGES.FETCH_ERROR);
        }
      );
    } else {
      setState(initialDialogState);
    }
  }, [componentId]);

  const handleError = (errorMessage) => {
    setState((prevState) => ({
      ...prevState,
      errorMessage,
      isLoading: false
    }));
  };

  const fetchVideoUrlFromBlob = async (videoBlobUrls) => {
    const videoPromises = videoBlobUrls.map((url) => fetch(url).then((res) => res.blob()));
    const blobs = await Promise.all(videoPromises);

    return blobs.map((blob) => {
      return URL.createObjectURL(blob);
    });
  };

  const handleClose = () => {
    navigate(location.pathname);
  };

  const { isLoading, videoURLs, errorMessage, assessmentData } = state;
  return (
    componentId && (
      <Dialog
        open
        classes={{ paper: dialogClasses ?? classes.dialog }}
        className={theme}>
        <DialogContent classes={{ root: classes.dialogContent }}>
          {isLoading ? (
            <div className={classes.loaderContainer}>
              <AppLoading />
            </div>
          ) : (
            <AppTutorial
              onClose={handleClose}
              videoURL={videoURLs[0]} // Supports only single video for a component right now so passing 1st element.
              assessmentData={assessmentData}
              errorMessage={errorMessage}
              userProfile={props.userData.userProfile}
              componentId={componentId}
            />
          )}
        </DialogContent>
      </Dialog>
    )
  );
};

export default compose(withUserProfile, withStyles(styles))(TutorialDialog);
